/**
 * My performance — month to date, against target, customer by customer.
 *
 * Targets carry three measures (cartons, value, customer count) because the
 * client sets all three. Showing only the value would hide a month where the
 * money came from two large shops and forty were never visited.
 *
 * Progress bars are capped at 100% visually but the number is not: a
 * salesperson at 140% should see 140%.
 *
 * The incentive figure is display-only. It is calculated outside the system,
 * so this screen shows what was published and says where it came from rather
 * than implying the app worked it out.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Page } from '../../components/Shell';
import {
  Banner, Card, Chip, Empty, ErrorState, Money, Skeleton, Tile, DataTable, type Column,
} from '../../components/ui';
import { useQuery, useRefreshOnFocus } from '../../lib/useApi';
import { useT } from '../../lib/i18n';
import { money, dateShort, daysBetween, businessDate } from '../../lib/format';

interface TargetBlock {
  period: string;
  targets: {
    id: string; dimension_type: string; target_quantity: number;
    target_value_paise: number; target_customer_count: number;
    target_boxes: number;
    category_name: string | null; area_name: string | null; store_type_name: string | null;
    /** Measured on this target's own dimension — see api field.ts achievementFor. */
    achieved: { boxes: number; quantity: number; valuePaise: number; customerCount: number };
    achievementPercent: {
      boxes: number | null;
      quantity: number | null; value: number | null; customers: number | null };
  }[];
  achieved: { quantity: number; valuePaise: number; customerCount: number };
  achievementPercent: { quantity: number | null; value: number | null; customers: number | null } | null;
}

interface CustomerRow {
  id: string; legal_name: string; customer_code: string;
  order_count: number; value_paise: number;
  last_order_date: string | null; outstanding_paise: number;
}

interface Dashboard {
  month: string;
  monthToDate: {
    ordersBooked: number; orderValuePaise: number; cartonsSold: number;
    customersServed: number; newCustomers: number;
    collectionsCount: number; collectionsAmountPaise: number;
    display: { orderValue: string; collections: string };
  };
  targets: { daily: TargetBlock | null; monthly: TargetBlock | null };
  incentive: { amountPaise: number; display: string; basis: string | null } | null;
  customerWise: CustomerRow[];
}

export default function Performance() {
  const t = useT();
  const navigate = useNavigate();
  const [scope, setScope] = useState<'monthly' | 'daily'>('monthly');

  const { data, loading, error, refetch } = useQuery<Dashboard>('/api/field/dashboard');
  useRefreshOnFocus(refetch);

  if (loading && !data) return <Page title={t('nav.performance')}><Skeleton rows={4} height={80} /></Page>;
  if (error && !data) {
    return (
      <Page title={t('nav.performance')}>
        <ErrorState message={error.message} onRetry={refetch} />
      </Page>
    );
  }
  if (!data) return <Page title={t('nav.performance')}><Empty /></Page>;

  const mtd = data.monthToDate;
  const block = scope === 'monthly' ? data.targets.monthly : data.targets.daily;

  const columns: Column<CustomerRow>[] = [
    {
      key: 'name',
      header: 'Customer',
      render: (r) => (
        <div>
          <div className="t-semi">{r.legal_name}</div>
          <div className="t-xs t-muted">{r.customer_code}</div>
        </div>
      ),
    },
    { key: 'orders', header: 'Orders', numeric: true, render: (r) => r.order_count },
    { key: 'value', header: 'Value', numeric: true, render: (r) => <Money paise={r.value_paise} compact /> },
    {
      key: 'outstanding',
      header: 'Outstanding',
      numeric: true,
      render: (r) => (r.outstanding_paise > 0
        ? <Money paise={r.outstanding_paise} tone="danger" compact />
        : <span className="t-muted">—</span>),
    },
    {
      key: 'last',
      header: 'Last order',
      render: (r) => {
        if (!r.last_order_date) return <span className="t-muted">—</span>;
        const days = daysBetween(r.last_order_date, businessDate());
        return (
          <span className={days > 21 ? 't-danger' : ''}>
            {dateShort(r.last_order_date)}
            {days > 21 && <span className="t-xs"> ({days}d)</span>}
          </span>
        );
      },
    },
  ];

  return (
    <Page title={t('nav.performance')} subtitle={data.month}>
      <div className="stack">
        <div className="grid-auto">
          <Tile tone="teal" label="Order value" value={mtd.display.orderValue} meta={`${mtd.ordersBooked} orders`} />
          <Tile tone="amber" label="Cartons" value={mtd.cartonsSold} meta="This month" />
          <Tile
            tone="blue"
            label="Customers served"
            value={mtd.customersServed}
            meta={mtd.newCustomers > 0 ? `${mtd.newCustomers} new` : undefined}
          />
          <Tile
            tone="green"
            label="Collections"
            value={mtd.display.collections}
            meta={`${mtd.collectionsCount} receipts`}
          />
        </div>

        <div className="segment" role="tablist">
          <button type="button" aria-pressed={scope === 'monthly'} onClick={() => setScope('monthly')}>
            Monthly target
          </button>
          <button type="button" aria-pressed={scope === 'daily'} onClick={() => setScope('daily')}>
            Today
          </button>
        </div>

        {!block && (
          <Banner tone="info" title="No target set">
            <span className="t-sm">
              {scope === 'monthly'
                ? 'No monthly target has been set for you yet.'
                : 'No target for today.'}
            </span>
          </Banner>
        )}

        {block && (
          <Card className="stack-2">
            <div className="t-sm t-muted">{block.period}</div>

            {block.targets.map((target) => (
              <div key={target.id} className="stack-2">
                {target.dimension_type !== 'overall' && (
                  <Chip outline>
                    {target.category_name ?? target.area_name ?? target.store_type_name
                      ?? target.dimension_type.replace(/_/g, ' ')}
                  </Chip>
                )}

                {/*
                  Each target against ITS OWN dimension. Using the block's
                  headline figures here scored a category target on total
                  sales — see the note on TargetRow.achieved.
                */}
                {target.target_boxes > 0 && (
                  <Progress
                    label="Boxes"
                    achieved={String(target.achieved.boxes)}
                    target={String(target.target_boxes)}
                    percent={target.achievementPercent.boxes ?? 0}
                  />
                )}
                {target.target_value_paise > 0 && (
                  <Progress
                    label="Value"
                    achieved={money(target.achieved.valuePaise)}
                    target={money(target.target_value_paise)}
                    percent={target.achievementPercent.value ?? 0}
                  />
                )}
                {target.target_customer_count > 0 && (
                  <Progress
                    label="Customers"
                    achieved={String(target.achieved.customerCount)}
                    target={String(target.target_customer_count)}
                    percent={target.achievementPercent.customers ?? 0}
                  />
                )}
                {target.dimension_type !== 'overall' && (
                  <div className="t-xs t-faint">
                    Counted only within{' '}
                    {target.category_name ?? target.area_name ?? target.store_type_name
                      ?? target.dimension_type.replace(/_/g, ' ')}.
                  </div>
                )}
              </div>
            ))}
          </Card>
        )}

        {data.incentive && (
          <Card className="stack-2">
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <span className="t-semi">Incentive published</span>
              <Money paise={data.incentive.amountPaise} className="t-lg t-semi" tone="ok" />
            </div>
            <div className="t-xs t-muted">
              {data.incentive.basis ?? 'Calculated and published by the office.'}
            </div>
          </Card>
        )}

        <div className="t-semi">Customer-wise</div>
        <DataTable
          columns={columns}
          rows={data.customerWise}
          keyOf={(r) => r.id}
          onRowClick={(r) => navigate(`/customers/${r.id}`)}
          empty={<Empty title="No orders yet this month" />}
        />
      </div>
    </Page>
  );
}

/**
 * One measure against its target.
 *
 * The bar is clamped to 100% so the layout cannot break, while the label shows
 * the true figure — under-reporting an over-achievement would be its own kind
 * of wrong.
 */
function Progress({
  label, achieved, target, percent,
}: {
  label: string;
  achieved: string;
  target: string;
  percent: number;
}) {
  const tone = percent >= 100 ? 'ok' : percent >= 70 ? 'warn' : 'danger';
  return (
    <div>
      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 4 }}>
        <span className="t-sm t-muted">{label}</span>
        <span className="t-sm">
          <span className="t-semi">{achieved}</span>
          <span className="t-muted"> / {target}</span>
          <span className={`t-semi t-${tone}`}> · {percent}%</span>
        </span>
      </div>
      <div className="meter" role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100}>
        <div
          className={`meter__fill meter__fill--${tone}`}
          style={{ width: `${Math.min(100, Math.max(0, percent))}%` }}
        />
      </div>
    </div>
  );
}
